/* eslint-disable camelcase */
import { Grid } from '@mui/material';
import { Coordinate, HexColor } from '@nx-smartmonkey/shared/domain';
import {
  GeoFenceDomainProps,
  GeoFencePaths,
  GoogleMapGeoFences,
  HSpacer,
  MarkerDomainProps,
  MarkerDomainTypes,
  RoutalDefaultColors,
  RoutalLoader,
  RoutalPalette,
} from '@nx-smartmonkey/ui';
import hash from 'object-hash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import GeoFenceIcon from '../../../../assets/img/geo_fence_icon.svg';
import CrossIcon from '../../../../assets/svg/CrossIcon';
import { getRouteColor } from '../../../../constants/Colors';
import { Driver } from '../../../../domain/driver/Driver';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useGetMapDefaults } from '../../../../hooks/map/useGetMapDefaults';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetPlanEditingGeoFence } from '../../../../hooks/plan/useSetPlanEditingGeoFence';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveFlatProjects } from '../../../../hooks/project/useRetrieveFlatProjects';
import { useUpdateProjectGeoFence } from '../../../../hooks/project/useUpdateProjectGeoFence';
import { useSetActiveLanguage } from '../../../../hooks/translations/useSetActiveLanguage';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { GeoFenceMapper } from '../../../../mappers/geoFence/GeoFenceMapper';
import Button from '../../../../react-components/Button';
import { PlaceSearch } from '../../../../react-components/PlaceSearch';
import Text from '../../../../react-components/Text';
import configuration from '../../../../services/configuration';
import { PlannerGeoFencesCreateDialog } from '../../planner-geo-fences/PlannerGeoFencesCreateDialog';

const StyledCloseIcon = styled(CrossIcon)`
  width: 15px !important;
  height: 15px !important;
  font-size: 15px !important;
  color: #393939;
  cursor: pointer;
`;

const CloseIcon = ({ onClose }: { onClose?: (...args: any[]) => any }) => {
  return <StyledCloseIcon onClick={onClose} />;
};

const getDefaultGeoFence = (projectId: string) =>
  GeoFence.create({
    label: ``,
    project_id: projectId,
    enabled: true,
    color: HexColor.create(RoutalDefaultColors[0]),
    coordinates: [],
  });

type MapGeoFencesProps = {
  project: Project;
  planGeoFences: GeoFence[];
  creatingGeoFence: boolean;
  setCreatingGeoFence: (isDrawing: boolean) => void;
};

export const MapGeoFences = ({ project, planGeoFences, creatingGeoFence, setCreatingGeoFence }: MapGeoFencesProps) => {
  const { data: activeLanguage } = useSetActiveLanguage();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: planStops } = useRetrieveActivePlanStops();
  const { data: user } = useRetrieveUser();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();
  const { data: editingGeoFence, run: setEditingGeoFence } = useSetPlanEditingGeoFence();
  const { getZoom, getCoordinates } = useGetMapDefaults();
  const { run: refetchProjects } = useRetrieveFlatProjects({ user });
  const { run: refetchActivePlanGeoFences } = useRetrieveActivePlanGeoFences();
  const { run: updateGeoFence, success: editSuccess } = useUpdateProjectGeoFence();
  const [isDrawingGeoFenceStep, setIsDrawingGeoFenceStep] = useState<`select_option` | `draw` | undefined>(undefined);
  const [newGeoFence, setNewGeoFence] = useState<GeoFence | undefined>(undefined);

  useEffect(() => {
    if (creatingGeoFence && isDrawingGeoFenceStep !== `select_option`) {
      setIsDrawingGeoFenceStep(`select_option`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingGeoFence]);

  useEffect(() => {
    if (editSuccess) {
      setEditingGeoFence(undefined);
      refetchActivePlanGeoFences();
      refetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSuccess]);

  const markers = useMemo(() => {
    return [
      // DRIVERS MARKERS
      ...(planDrivers ?? []).reduce((markersAmount: MarkerDomainProps[], driver: Driver, idx: number) => {
        const routeColor = getRouteColor(driver as Driver, planDrivers ?? []);

        const driverSpecificMarkers: Array<MarkerDomainProps> = [];

        const driverStopsMarkers = driver.stops
          .filter((stop) => stop.location?.isValid())
          .map((stop: Stop) => ({
            id: stop.id,
            label: stop.label,
            color: routeColor,
            lat: stop.location!.lat!,
            lng: stop.location!.lng!,
            kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
            number: stop.order != null ? stop.order + 1 : undefined,
          }));

        return markersAmount.concat(driverStopsMarkers).concat(driverSpecificMarkers);
      }, []),

      // UNASSIGNED STOPS MARKERS
      ...((planStops ?? []).filter((stop: Stop) => !stop.driver_id && stop.location?.isValid()) ?? []).map(
        (stop: Stop) => ({
          id: stop.id,
          label: stop.label,
          lat: stop.location!.lat!,
          lng: stop.location!.lng!,
          kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
        })
      ),
    ];
  }, [planDrivers, planStops]);

  const geoFences = useMemo(() => {
    return (planGeoFences ?? []).map(
      (geoFence) =>
        ({
          id: geoFence.id,
          selected: geoFence.id === editingGeoFence?.id,
          editable: geoFence.id === editingGeoFence?.id,
          paths: geoFence.coordinatesToObject(),
          color: geoFence.color.value,
          eventsUpdateHash: hash({ id: geoFence.id, editingGeoFence: editingGeoFence?.id, isDrawingGeoFenceStep }),
          onPathChanged: (event: any, geoFenceId: string, newPaths: GeoFencePaths) => {
            if (editingGeoFence) {
              const newCoordinates: Coordinate[][] = [];
              newPaths.forEach((path) => {
                const newPathCoords = path.map((coordinate) => {
                  const coord = Coordinate.create({ lat: coordinate.lat, lng: coordinate.lng });
                  return coord;
                });

                if (
                  newPathCoords.length > 0 &&
                  (newPathCoords[newPathCoords.length - 1].lat !== newPathCoords[0].lat ||
                    newPathCoords[newPathCoords.length - 1].lng !== newPathCoords[0].lng)
                ) {
                  newPathCoords.push(newPathCoords[0]);
                }

                newCoordinates.push(newPathCoords);
              });
              const newGeoFence = GeoFenceMapper.cloneDomain(editingGeoFence!);
              newGeoFence.coordinates = newCoordinates;
              setEditingGeoFence(newGeoFence);
            }
          },
        } as GeoFenceDomainProps)
    );
  }, [editingGeoFence, isDrawingGeoFenceStep, planGeoFences, setEditingGeoFence]);

  const onSaveEditingGeoFence = () => {
    if (editingGeoFence) {
      updateGeoFence({ geoFence: editingGeoFence });
    }
  };

  const onCancelEditingGeoFence = () => {
    setEditingGeoFence(undefined);
  };

  const getCreateButtons = () => {
    if (!isDrawingGeoFenceStep) {
      return null;
    }
    switch (isDrawingGeoFenceStep) {
      case `select_option`:
        return (
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{
              padding: `8px`,
              borderRadius: `4px`,
              backgroundColor: `${RoutalPalette.neutral00}bd`,
              border: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <Grid item style={{ width: `300px` }}>
              <PlaceSearch
                placeholder={intl.formatMessage({
                  id: `location`,
                })}
                // TODO: noOptionsMessage
                onUpdate={({
                  placeLabel,
                  placeCoordinates,
                }: {
                  placeLabel?: string;
                  placeCoordinates?: [number, number][][][];
                }) => {
                  if (placeLabel && placeCoordinates) {
                    const geoFenceToCreate = getDefaultGeoFence(project.id);
                    geoFenceToCreate.label = placeLabel;

                    geoFenceToCreate.coordinates = placeCoordinates.map((placeCoordinate) =>
                      placeCoordinate[0].map((coords) => Coordinate.fromArray([coords[1], coords[0]]))
                    );
                    setNewGeoFence(geoFenceToCreate);
                  }
                  setIsDrawingGeoFenceStep(undefined);
                }}
              />
            </Grid>

            <HSpacer small />

            <Button
              styleType="secondary"
              style={{ margin: 0, padding: 0, width: `32px`, height: `32px` }}
              onClick={() => {
                setCreatingGeoFence(true);
                setIsDrawingGeoFenceStep(`draw`);
              }}
            >
              <img style={{ cursor: `pointer` }} width="20" src={GeoFenceIcon} alt="" draggable="false" />
            </Button>

            <HSpacer small />

            <CloseIcon
              onClose={() => {
                if (creatingGeoFence) {
                  setCreatingGeoFence(false);
                } else {
                  setEditingGeoFence(undefined);
                }
              }}
            />
          </Grid>
        );
      case `draw`:
        if (!newGeoFence) {
          return (
            <Button
              styleType="secondary"
              onClick={() => {
                setCreatingGeoFence(false);
                setIsDrawingGeoFenceStep(undefined);
              }}
              intlId="cancel"
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      <div
        style={{
          position: `relative`,
          width: `100%`,
          height: `100%`,
        }}
      >
        {editingGeoFence !== undefined || isDrawingGeoFenceStep === `draw` ? (
          <div
            style={{
              position: `absolute`,
              bottom: `20px`,
              minHeight: `50px`,
              width: `100%`,
              zIndex: 1,
              display: `flex`,
              justifyContent: `center`,
            }}
          >
            <div
              style={{
                border: `1px solid ${RoutalPalette.neutral10}`,
                borderRadius: `2px`,
                width: `500px`,
                maxWidth: `100%`,
                backgroundColor: RoutalPalette.neutral02,
                padding: `8px`,
              }}
            >
              {isDrawingGeoFenceStep === `draw` ? (
                <Text intlId="map_options.zones.hints.create_draw" />
              ) : (
                <Text intlId="map_options.zones.hints.edit" />
              )}
            </div>
          </div>
        ) : null}

        {isDrawingGeoFenceStep !== undefined ? (
          <Grid
            item
            container
            wrap="nowrap"
            justifyContent="flex-end"
            alignItems="center"
            style={{
              position: `absolute`,
              top: `9px`,
              zIndex: 1,
              width: `auto`,
              right: `9px`,
            }}
          >
            <Grid item>{getCreateButtons()}</Grid>
          </Grid>
        ) : (
          <>
            {editingGeoFence !== undefined ? (
              <Grid
                item
                container
                wrap="nowrap"
                justifyContent="flex-end"
                alignItems="center"
                style={{
                  position: `absolute`,
                  top: `9px`,
                  zIndex: 1,
                  width: `auto`,
                  right: `9px`,
                }}
              >
                <Grid item>
                  <Grid container direction="row">
                    <Button styleType="secondary" onClick={() => onCancelEditingGeoFence()} intlId="cancel" />
                    <HSpacer small />
                    <Button onClick={() => onSaveEditingGeoFence()} intlId="save" />
                  </Grid>
                </Grid>
              </Grid>
            ) : null}
          </>
        )}

        <div style={{ width: `100%`, height: `100%` }}>
          <GoogleMapGeoFences
            height="100%"
            disableDoubleClickZoom
            language={activeLanguage?.locale ?? `en`}
            apiKey={configuration.GOOGLE_MAPS_API_KEY}
            loadingComponent={<RoutalLoader width={60} />}
            center={(projectGeoFences ?? []).length === 0 ? getCoordinates() : undefined}
            zoomLevel={(projectGeoFences ?? []).length === 0 ? getZoom() : undefined}
            isDrawing={isDrawingGeoFenceStep === `draw`}
            onGeoFenceDrawn={(coordinates: { lat: number; lng: number }[]) => {
              const geoFenceToCreate = getDefaultGeoFence(project.id);
              geoFenceToCreate.coordinates = [coordinates.map((c) => Coordinate.create(c))];
              setNewGeoFence(geoFenceToCreate);
            }}
            geoFences={geoFences}
            markers={markers}
          />
        </div>
      </div>

      {newGeoFence ? (
        <PlannerGeoFencesCreateDialog
          project={project}
          newGeoFence={newGeoFence}
          setNewGeoFence={setNewGeoFence}
          onClose={(created: boolean) => {
            setNewGeoFence(undefined);
            setIsDrawingGeoFenceStep(undefined);

            if (created) {
              refetchActivePlanGeoFences();
              refetchProjects();
            }
            setCreatingGeoFence(false);
          }}
        />
      ) : null}
    </>
  );
};
